'use client'

import s from './Slider.module.scss'
import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import LazyImage from '@/components/Common/LazyImage'

type Img = { imageUrl: string; filename: string; alt?: string; metadata?: { dimensions?: { width?: number; height?: number } } }
type Props = { data: { images: Img[] } }

export default function Slider({ data }: Props) {
  const images = data?.images || []
  const len = images.length
  const [index, setIndex] = useState(0)
  const [hoverSide, setHoverSide] = useState<'left' | 'right' | null>(null)
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const touch = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const check = () => setIsMobile(window.innerWidth <= 990)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const prevIndex = (index - 1 + len) % len
  const nextIndex = (index + 1) % len

  const goNext = () => len > 1 && setIndex(i => (i + 1) % len)
  const goPrev = () => len > 1 && setIndex(i => (i - 1 + len) % len)

  const fade = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit:    { opacity: 0 },
  }

  const w = (img: Img) => img?.metadata?.dimensions?.width ?? 800
  const h = (img: Img) => img?.metadata?.dimensions?.height ?? 600

  const getSide = (clientX: number) => {
    const el = containerRef.current
    if (!el) return null
    const rect = el.getBoundingClientRect()
    return clientX < rect.left + rect.width / 2 ? 'left' : 'right'
  }

  const onMove = (e: React.MouseEvent) => {
    if (isMobile) return // en mobile paso de hover
    setHoverSide(getSide(e.clientX))
    setMousePos({ x: e.clientX, y: e.clientY })
  }

  const onLeave = () => { setHoverSide(null); setMousePos(null) }
  const onClick = (e: React.MouseEvent) => {
    if (isMobile) return // en mobile ya tengo botones / swipe
    getSide(e.clientX) === 'left' ? goPrev() : goNext()
  }

  // touch (tap izq/der + swipe)
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0]
    const start = touch.current; touch.current = null
    if (!start) return
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    if (Math.abs(dx) < 20 && Math.abs(dy) < 20) {
      getSide(t.clientX) === 'left' ? goPrev() : goNext()
    } else if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
      dx < 0 ? goNext() : goPrev()
    }
  }

  useEffect(() => {
    if (len < 2) return
    const nextUrl = images[(index + 1) % len]?.imageUrl
    if (nextUrl) {
      const im = new Image()
      im.src = nextUrl
    }
  }, [index, len, images])

  return (
    <div
      ref={containerRef}
      className={s.wrapper}
      data-side={hoverSide || ''}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className={s.grid}>
        {/* SLOT PREV */}
        <div className={s.slotPrev} style={{ aspectRatio: `${w(images[prevIndex])} / ${h(images[prevIndex])}` }}>
          <AnimatePresence mode="sync" initial={false}>
            <motion.figure
              key={`prev-${prevIndex}`}
              className={s.figure}
              variants={fade}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              <LazyImage
                src={images[prevIndex].imageUrl}
                alt={images[prevIndex].alt}
                width={w(images[prevIndex])}
                height={h(images[prevIndex])}
                filename={images[prevIndex].filename}
              />
            </motion.figure>
          </AnimatePresence>
        </div>

        {/* SLOT ACTIVO */}
        <div className={s.slotActive} style={{aspectRatio: `${w(images[index])} / ${h(images[index])}`}}>
          <AnimatePresence mode="sync" initial={false}>
            <motion.figure
              key={`active-${index}`}
              className={s.figure}
              variants={fade}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.6, ease: 'easeInOut' }}
            >
              <LazyImage
                src={images[index].imageUrl}
                alt={images[index].alt}
                width={w(images[index])}
                height={h(images[index])}
                filename={images[index].filename}
              />
            </motion.figure>
          </AnimatePresence>
        </div>

        {/* SLOT NEXT */}
        <div className={s.slotNext} style={{aspectRatio: `${w(images[nextIndex])} / ${h(images[nextIndex])}`}}>
          <AnimatePresence mode="sync" initial={false}>
            <motion.figure
              key={`next-${nextIndex}`}
              className={s.figure}
              variants={fade}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              <LazyImage
                src={images[nextIndex].imageUrl}
                alt={images[nextIndex].alt}
                width={w(images[nextIndex])}
                height={h(images[nextIndex])}
                filename={images[nextIndex].filename}
              />
            </motion.figure>
          </AnimatePresence>
        </div>
      </div>

      {/* Cursor label SOLO desktop */}
      {!isMobile && mousePos && hoverSide && (
        <div
          className={s.cursorLabel}
          data-side={hoverSide}
          style={{ transform: `translate3d(${mousePos.x}px, ${mousePos.y}px, 0)` }}
          aria-hidden="true"
        >
          {hoverSide === 'left' ? 'Prev' : 'Next'}
        </div>
      )}

      {/* Overlay de mitades opcional (solo desktop) */}
      {!isMobile && (
        <div className={s.halves} aria-hidden="true">
          <div className={s.leftHalf} />
          <div className={s.rightHalf} />
        </div>
      )}

      {/* Botones abajo SOLO mobile */}
      {isMobile && len > 1 && (
        <div className={s.navButtons}>
          <button type="button" onClick={goPrev}>Prev</button>
          <button type="button" onClick={goNext}>Next</button>
        </div>
      )}
    </div>
  )
}